import { isImpersonated } from '@/redux/modules/auth/userMini';
import { getSegment } from '@/services/segment';
import { getCookie } from '@/utility/cookie';
import { isSSR } from '@/utility/isSSR';

import { getODNEventProperties } from './campground/util';
import { ECheckoutEventName } from './checkout/types';
import { EListingEventName } from './listings/types';
import { ESearchEventName } from './search/types';
import { getFBPixelProps } from './utils';

export enum EProjectSection {
  OWNER = 'owner',
  MARKETPLACE = 'marketplace',
  MARKETPLACE_DASHBOARD = 'marketplace_dashboard',
  WHEELBASE_DASHBOARD = 'wheelbase_dashboard',
  WHEELBASE_API = 'wheelbase_api',
  WHELLBASE_EMBED = 'wheelbase_embed',
  ROAMLY = 'roamly',
}

export async function trackEventV2<
  E extends string,
  P extends Record<string, unknown>,
  S = EProjectSection,
>(eventName: E, projectSection: S, extraInfo: P): Promise<void> {
  if (isSSR()) return;
  const impersonated = isImpersonated() ? { isImpersonated: true } : {};
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const universalFields = {
    platform: 'web',
    pageUrl: window.location.pathname,
    property: projectSection,
    legacyDeviceID: getCookie('odc-d'),
    cam: urlParams.get('cam'),
    subcam: urlParams.get('subcam'),
    ...impersonated,
  };
  // Movable ink requires certain fields to be strings, and segment can't convert data types
  const moveableInkEvents = [
    'Search Results Viewed',
    'Listing Viewed',
    'Booking Request Contact Started',
    'Booking Request Payment Completed',
  ];
  const stringFields = [
    'rentalID',
    'listingID',
    'rentalIDs',
    'listingIDs',
    'bookingID',
    'bookingIDs',
    'campgroundID',
    'campgroundIDs',
  ];
  const extendedExtraInfo = moveableInkEvents.includes(eventName)
    ? stringFields.reduce<Record<string, unknown>>(
        (acc, field) => {
          if (field in acc && !('string_' + field in acc)) {
            const value = acc[field];
            if (Array.isArray(value)) {
              acc['string_' + field] = value.map(f => `${f}`);
            } else {
              acc['string_' + field] = `${value}`;
            }
          }
          return acc;
        },
        { ...extraInfo },
      )
    : extraInfo;

  const infoWithFBPixelProps = attachFBPixelProps(eventName, extendedExtraInfo);
  const odnInfo = getODNEventProperties(extendedExtraInfo);
  const analytics = getSegment();
  if (!analytics) return;

  const finalEventData = { ...universalFields, ...infoWithFBPixelProps, ...odnInfo };
  analytics.track(eventName, finalEventData);

  return;
}

const EVENTS_TO_ATTACH_FB_PIXEL_PROPS: string[] = [
  ECheckoutEventName.CHECKOUT_BOOKING_REQUEST_QUESTIONS_STARTED,
  ECheckoutEventName.CHECKOUT_BOOKING_REQUEST_PAYMENT_COMPLETED,
  ESearchEventName.SEARCH_RESULT_VIEWED,
  EListingEventName.LISTING_VIEWED,
];

const EVENTS_TO_ATTACH_FB_PIXEL_PROPS_CONTENT_TYPE: string[] = [
  ECheckoutEventName.CHECKOUT_BOOKING_REQUEST_QUESTIONS_STARTED,
  ECheckoutEventName.CHECKOUT_BOOKING_REQUEST_PAYMENT_COMPLETED,
  EListingEventName.LISTING_VIEWED,
];

function attachFBPixelProps<E extends string, P extends Record<string, unknown>>(
  eventName: E,
  extraInfo: P,
): P {
  if (!extraInfo) {
    return extraInfo;
  }
  if (!EVENTS_TO_ATTACH_FB_PIXEL_PROPS.includes(eventName)) {
    return extraInfo;
  }
  const fbPixelFields = getFBPixelProps();
  if (EVENTS_TO_ATTACH_FB_PIXEL_PROPS_CONTENT_TYPE.includes(eventName)) {
    fbPixelFields.contentType = 'product';
  }

  return {
    ...extraInfo,
    ...fbPixelFields,
    content_type: 'product',
    content_ids: extraInfo.rentalID,
  };
}

// we can no longer await analytics due to the consent banner
// instead when navigating with external routing (window.location.href)
// use a timer to provide a small delay for the event to be sent
export const delayTimerForAnalytics = (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms));
